import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

export default function FileUploadDropzone({
  disabled = false,
  multiple = true,
  files = [],                // controlled files
  onFilesChange,             // callback
  onPreview,                 // optional preview handler
  supportedTypes = [],
  maxFileSizeMB = 10,
  helperText
}) {
  const [error, setError] = useState(null);
  const maxBytes = maxFileSizeMB * 1024 * 1024;

  const validateFiles = (incomingFiles) => {
    const validFiles = [];

    incomingFiles.forEach((file) => {
      if (
        supportedTypes.length &&
        !supportedTypes.includes(file.type)
      ) {
        setError(`Unsupported file type: ${file.name}`);
        return;
      }

      if (file.size > maxBytes) {
        setError(`${file.name} exceeds ${maxFileSizeMB}MB`);
        return;
      }

      validFiles.push({
        id: `${file.name}-${Date.now()}`,
        file,
        name: file.name,
        type: file.type
      });
    });

    if (validFiles.length) {
      setError(null);
      onFilesChange([...files, ...validFiles]);
    }
  };

  const handleRemove = (id) => {
    onFilesChange(files.filter((f) => f.id !== id));
  };

  return (
    <Box>
      {/* DROPZONE */}
      <Box
        mt={2}
        p={2}
        border="2px dashed #ccc"
        borderRadius={2}
        textAlign="center"
        sx={{ opacity: disabled ? 0.5 : 1 }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (!disabled)
            validateFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <UploadFileIcon />
        <Typography variant="body2">Drag & drop files</Typography>

        <Button
          component="label"
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
          disabled={disabled}
        >
          Browse Files
          <input
            hidden
            type="file"
            multiple={multiple}
            onChange={(e) =>
              validateFiles(Array.from(e.target.files))
            }
          />
        </Button>

        {helperText && (
          <Typography
            variant="caption"
            display="block"
            mt={1}
            color="text.secondary"
          >
            {helperText}
          </Typography>
        )}
      </Box>

      {/* ERROR */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* FILE LIST */}
      {files.length > 0 && (
        <List dense sx={{ mt: 2, maxHeight: 180, overflow: "auto" }}>
          {files.map((f) => (
            <ListItem
              key={f.id}
              secondaryAction={
                <IconButton onClick={() => handleRemove(f.id)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={f.name}
                onClick={() => onPreview?.(f)}
                sx={{ cursor: onPreview ? "pointer" : "default" }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
